import { FunctionComponent } from "react";
import { graphql } from "@apollo/client/react/hoc";

import {
    SETS_QUERY,
    CREATE_SET_MUTATION,
    UPDATE_SET,
    DELETE_SET_MUTATION,
    UPDATE_SET_METADATA_TAGS,
} from "../graphql";
import { captureException } from "@sentry/react";

// Query
export const withSets = (Component: FunctionComponent) =>
    graphql(SETS_QUERY, {
        options: ({ where, pagination }) => {
            return {
                variables: {
                    where,
                    ...pagination,
                },
            };
        },
        props: ({ data }) => {
            const {
                loading,
                error,
                sets,
                fetchMore,
                subscribeToMore,
                updateQuery,
                refetch,
            } = data;
            const loadSetsData = (offset: number) => {
                return fetchMore({
                    variables: {
                        offset,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        return {
                            sets: [
                                ...previousResult.sets,
                                ...fetchMoreResult.sets,
                            ],
                        };
                    },
                });
            };
            if (error) throw new Error(error.message);
            return {
                loading,
                sets,
                subscribeToMore,
                updateQuery,
                loadSetsData,
                refetchSets: refetch,
            };
        },
    })(Component);

// Mutation
export const withCreateSet = (Component: FunctionComponent) =>
    graphql(CREATE_SET_MUTATION, {
        props: ({ mutate }) => ({
            createSet: async (object: Object) => {
                // console.log(
                //     "🚀 ~ file: Operations.ts ~ line 60 ~ createSet: ~ object",
                //     object,
                // );
                try {
                    const {
                        data: { insert_set_one },
                    } = await mutate({
                        variables: { object },
                        optimisticResponse: {
                            __typename: "Mutation",
                            createSet: {
                                object,
                                __typename: "insert_set_one",
                            },
                        },
                    });
                    return insert_set_one;
                } catch (e) {
                    captureException(e)
                    console.error(e);
                }
            },
        }),
    })(Component);

export const withUpdateSet = (Component: FunctionComponent) =>
    graphql(UPDATE_SET, {
        props: ({ mutate }) => ({
            updateSet: (object: Object) => {
                mutate({
                    variables: { ...object },
                    optimisticResponse: {
                        updateSet: {
                            ...object,
                            __typename: "set",
                        },
                    },
                });
            },
        }),
    })(Component);

export const withUpdateSetMetadataTags = (Component: FunctionComponent) =>
    graphql(UPDATE_SET_METADATA_TAGS, {
        props: ({ mutate }) => ({
            updateSet: (object: Object) => {
                mutate({
                    variables: { ...object },
                    optimisticResponse: {
                        updateSet: {
                            ...object,
                            __typename: "set",
                        },
                    },
                });
            },
        }),
    })(Component);

export const withDeleteSet = (Component: FunctionComponent) =>
    graphql(DELETE_SET_MUTATION, {
        props: ({ mutate }) => ({
            deleteSet: ({ id, submissionIds }) => {
                mutate({
                    variables: { id, submissionIds },
                    optimisticResponse: {
                        __typename: "Mutation",
                        deleteSet: {
                            id,
                            __typename: "DELETE_SET_BY_PK",
                        },
                    },
                });
            },
        }),
    })(Component);
